import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Lightbulb, 
  Send, 
  Loader2,
  MessageSquare,
  CheckCircle2,
  Clock,
  ArrowRight,
  X,
  Camera,
  Image as ImageIcon,
  ExternalLink,
  Archive
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const ImprovementSuggestionsAdmin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [suggestions, setSuggestions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [filterTab, setFilterTab] = useState('active'); // NEW: 'active', 'archived', 'all'
  
  // Forward dialog
  const [forwardDialog, setForwardDialog] = useState(null);
  const [forwardComment, setForwardComment] = useState('');
  const [forwarding, setForwarding] = useState(false);
  
  // Reply dialog
  const [replyDialog, setReplyDialog] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);
  
  // Create suggestion dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newSuggestion, setNewSuggestion] = useState({ title: '', description: '', location_id: '', photos: [] });
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);
  
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [suggestionsRes, locationsRes] = await Promise.all([
        axios.get(`${API}/api/improvement-suggestions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        axios.get(`${API}/api/admin/locations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setSuggestions(suggestionsRes.data || []);
      setLocations(locationsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleForward = async () => {
    if (!forwardDialog) return;
    
    setForwarding(true);
    try {
      const token = localStorage.getItem('token');
      
      // Send admin_comment in request body, not as query param
      const requestData = forwardComment ? { admin_comment: forwardComment } : {};
      
      await axios.put(
        `${API}/api/improvement-suggestions/${forwardDialog.id}/forward`,
        requestData,
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      toast.success('Vorschlag erfolgreich an Superadmin weitergeleitet');
      setForwardDialog(null);
      setForwardComment('');
      fetchData();
    } catch (error) {
      console.error('Failed to forward:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Weiterleiten');
    } finally {
      setForwarding(false);
    }
  };

  const handleReply = async () => {
    if (!replyDialog || !replyMessage.trim()) return;
    
    setReplying(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/improvement-suggestions/${replyDialog.id}/reply`, {
        message: replyMessage.trim()
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Antwort gesendet');
      setReplyDialog(null);
      setReplyMessage('');
      fetchData();
    } catch (error) {
      console.error('Failed to reply:', error);
      toast.error('Fehler beim Senden der Antwort');
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async (suggestionId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/improvement-suggestions/${suggestionId}/status`, null, {
        params: { status: newStatus },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      toast.success('Status aktualisiert');
      fetchData();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Fehler beim Aktualisieren');
    }
  };

  const handlePhotoUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length + newSuggestion.photos.length > 3) {
      toast.error('Maximal 3 Fotos erlaubt');
      return;
    }

    const uploadedUrls = [];
    for (const file of files) {
      const reader = new FileReader();
      reader.onloadend = () => {
        uploadedUrls.push(reader.result);
        if (uploadedUrls.length === files.length) {
          setNewSuggestion(prev => ({ ...prev, photos: [...prev.photos, ...uploadedUrls] }));
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitSuggestion = async () => {
    if (!newSuggestion.title.trim() || !newSuggestion.description.trim() || !newSuggestion.location_id) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/improvement-suggestions`, newSuggestion, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Vorschlag erfolgreich eingereicht');
      setShowCreateDialog(false);
      setNewSuggestion({ title: '', description: '', location_id: '', photos: [] });
      fetchData();
    } catch (error) {
      console.error('Failed to submit suggestion:', error);
      toast.error('Fehler beim Einreichen');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status, archived) => {
    if (archived) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-800">
          <Archive className="h-3 w-3" />
          Archiviert
        </span>
      );
    }
    
    const badges = {
      pending: { text: 'Ausstehend', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      reviewed: { text: 'Geprüft', color: 'bg-blue-100 text-blue-800', icon: CheckCircle2 },
      forwarded: { text: 'Weitergeleitet', color: 'bg-purple-100 text-purple-800', icon: ArrowRight },
      implemented: { text: 'Umgesetzt', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      rejected: { text: 'Abgelehnt', color: 'bg-red-100 text-red-800', icon: X }
    };
    
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="h-3 w-3" />
        {badge.text}
      </span>
    );
  };

  const getFilteredSuggestions = () => {
    let filtered = suggestions;
    
    // Apply archive filter
    if (filterTab === 'active') {
      filtered = filtered.filter(s => !s.archived);
    } else if (filterTab === 'archived') {
      filtered = filtered.filter(s => s.archived);
    }
    
    // Apply status filter
    if (activeTab === 'new') {
      filtered = filtered.filter(s => s.status === 'pending' && !s.archived);
    } else if (activeTab === 'forwarded') {
      filtered = filtered.filter(s => s.forwarded_to_superadmin);
    }
    
    return filtered;
  };

  const filteredSuggestions = getFilteredSuggestions();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-4 p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <Lightbulb className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-500" />
              Verbesserungsvorschläge
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Mitarbeiter-Ideen verwalten</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="bg-yellow-600 hover:bg-yellow-700">
            <Send className="h-4 w-4 mr-2" />
            Neuen Vorschlag erstellen
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b overflow-x-auto">
          <button
            onClick={() => setActiveTab('new')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              activeTab === 'new'
                ? 'border-b-2 border-yellow-600 text-yellow-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Neu ({suggestions.filter(s => s.status === 'pending' && !s.archived).length})
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              activeTab === 'all'
                ? 'border-b-2 border-yellow-600 text-yellow-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Alle ({suggestions.length})
          </button>
          <button
            onClick={() => setActiveTab('forwarded')}
            className={`px-4 py-2 text-sm font-medium whitespace-nowrap ${
              activeTab === 'forwarded'
                ? 'border-b-2 border-yellow-600 text-yellow-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Weitergeleitet ({suggestions.filter(s => s.forwarded_to_superadmin).length})
          </button>
        </div>

        {/* Archive Filter Tabs */}
        <div className="flex gap-2 border-b overflow-x-auto">
          <button
            onClick={() => setFilterTab('active')}
            className={`px-3 py-1 text-xs font-medium whitespace-nowrap ${
              filterTab === 'active'
                ? 'border-b-2 border-gray-600 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Aktiv ({suggestions.filter(s => !s.archived).length})
          </button>
          <button
            onClick={() => setFilterTab('archived')}
            className={`px-3 py-1 text-xs font-medium whitespace-nowrap ${
              filterTab === 'archived'
                ? 'border-b-2 border-gray-600 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Archiviert ({suggestions.filter(s => s.archived).length})
          </button>
          <button
            onClick={() => setFilterTab('all')}
            className={`px-3 py-1 text-xs font-medium whitespace-nowrap ${
              filterTab === 'all'
                ? 'border-b-2 border-gray-600 text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Alle
          </button>
        </div>

        {/* Suggestions List */}
        <div className="space-y-3">
          {filteredSuggestions.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Lightbulb className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Keine Vorschläge vorhanden</p>
              </CardContent>
            </Card>
          ) : (
            filteredSuggestions.map(suggestion => (
              <Card key={suggestion.id} className={`hover:shadow-md transition-shadow ${
                suggestion.archived ? 'bg-gray-50' : ''
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 dark:text-gray-100">{suggestion.title}</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Von: {suggestion.submitted_by_name} • {suggestion.location_name}
                      </p>
                    </div>
                    {getStatusBadge(suggestion.status, suggestion.archived)}
                  </div>
                  
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">{suggestion.description}</p>
                  
                  {/* Archive Info */}
                  {suggestion.archived && suggestion.archived_reason && (
                    <div className="mb-3 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs text-gray-700 dark:text-gray-300">
                      <strong>Archivierungsgrund:</strong> {suggestion.archived_reason}
                    </div>
                  )}
                  
                  {suggestion.photos && suggestion.photos.length > 0 && (
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {suggestion.photos.map((photo, idx) => (
                        <img
                          key={idx}
                          src={photo}
                          alt={`Foto ${idx + 1}`}
                          className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-75"
                          onClick={() => window.open(photo, '_blank')}
                        />
                      ))}
                    </div>
                  )}

                  {suggestion.admin_comment && (
                    <div className="bg-blue-50 rounded p-3 mb-3">
                      <p className="text-xs font-medium text-blue-900">Admin-Kommentar:</p>
                      <p className="text-sm text-blue-800 mt-1">{suggestion.admin_comment}</p>
                    </div>
                  )}

                  {suggestion.replies && suggestion.replies.length > 0 && (
                    <div className="mb-3 space-y-2">
                      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Antworten:</p>
                      {suggestion.replies.map(reply => (
                        <div key={reply.id} className="bg-gray-50 dark:bg-gray-800 rounded p-2">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">{reply.user_name}</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{reply.message}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions - Only for non-archived */}
                  {!suggestion.archived && (
                    <div className="flex flex-wrap gap-2 pt-3 border-t">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReplyDialog(suggestion)}
                        className="flex-1 sm:flex-none"
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Antworten
                      </Button>
                      
                      {!suggestion.forwarded_to_superadmin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setForwardDialog(suggestion)}
                          className="flex-1 sm:flex-none"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          Weiterleiten
                        </Button>
                      )}
                      
                      <select
                        value={suggestion.status}
                        onChange={(e) => handleStatusChange(suggestion.id, e.target.value)}
                        className="text-sm px-2 py-1 border rounded"
                      >
                        <option value="pending">Ausstehend</option>
                        <option value="reviewed">Geprüft</option>
                        <option value="implemented">Umgesetzt</option>
                        <option value="rejected">Abgelehnt</option>
                      </select>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Forward Dialog */}
        <Dialog open={!!forwardDialog} onOpenChange={() => setForwardDialog(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>An Superadmin weiterleiten</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm font-medium">Vorschlag: {forwardDialog?.title}</p>
                <p className="text-xs text-gray-500">Von: {forwardDialog?.submitted_by_name}</p>
              </div>
              <div className="space-y-2">
                <Label>Ihr Kommentar (optional)</Label>
                <Textarea
                  value={forwardComment}
                  onChange={(e) => setForwardComment(e.target.value)}
                  placeholder="Fügen Sie einen Kommentar hinzu..."
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setForwardDialog(null)}>
                Abbrechen
              </Button>
              <Button onClick={handleForward} disabled={forwarding}>
                {forwarding ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Weiterleiten'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reply Dialog */}
        <Dialog open={!!replyDialog} onOpenChange={() => setReplyDialog(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Antwort senden</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm font-medium">{replyDialog?.title}</p>
              </div>
              <div className="space-y-2">
                <Label>Ihre Nachricht</Label>
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Schreiben Sie eine Antwort..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReplyDialog(null)}>
                Abbrechen
              </Button>
              <Button onClick={handleReply} disabled={replying || !replyMessage.trim()}>
                {replying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Senden'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Suggestion Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Neuen Verbesserungsvorschlag erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Standort *</Label>
                <select
                  value={newSuggestion.location_id}
                  onChange={(e) => setNewSuggestion(prev => ({ ...prev, location_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-md"
                >
                  <option value="">Standort auswählen</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Titel *</Label>
                <Input
                  value={newSuggestion.title}
                  onChange={(e) => setNewSuggestion(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Kurze Zusammenfassung Ihres Vorschlags"
                />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung *</Label>
                <Textarea
                  value={newSuggestion.description}
                  onChange={(e) => setNewSuggestion(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Beschreiben Sie Ihren Vorschlag detailliert..."
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label>Fotos (optional, max. 3)</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={newSuggestion.photos.length >= 3}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Foto aufnehmen
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </div>
                {newSuggestion.photos.length > 0 && (
                  <div className="flex gap-2 flex-wrap mt-2">
                    {newSuggestion.photos.map((photo, idx) => (
                      <div key={idx} className="relative">
                        <img src={photo} alt={`Foto ${idx + 1}`} className="w-20 h-20 object-cover rounded" />
                        <button
                          onClick={() => setNewSuggestion(prev => ({ 
                            ...prev, 
                            photos: prev.photos.filter((_, i) => i !== idx) 
                          }))}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowCreateDialog(false);
                setNewSuggestion({ title: '', description: '', location_id: '', photos: [] });
              }}>
                Abbrechen
              </Button>
              <Button onClick={handleSubmitSuggestion} disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Absenden'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};